import { twMerge } from "tailwind-merge";
import { AffiliationRow, type AffiliationEntry } from "../AffiliationRow";
import type { OptionType } from "@themes/pollinator/ui/Select";

export type { AffiliationEntry };

export type Props = {
  /** Field name prefix for the inputs */
  name: string;
  /** Current value - array of org/role entries */
  value?: AffiliationEntry[];
  /** Change handler - receives updated entries array */
  onChange?: (value: AffiliationEntry[]) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error */
  error?: boolean;
  /** ID of the element holding the field's error, read out with each select */
  "aria-describedby"?: string;
  /** Whether an answer is required, as a screen reader is told */
  "aria-required"?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Maximum number of affiliation entries */
  maxItems?: number;
  /** Minimum number of visible affiliation rows */
  minItems?: number;
  /** Additional CSS classes */
  className?: string;
  /** Organization options for select */
  organizationOptions?: OptionType[];
  /** Role options for select */
  roleOptions?: OptionType[];
  /** Whether options are loading */
  isLoading?: boolean;
  /** Handler for creating new organization */
  onCreateOrganization?: (name: string) => void;
  /** Whether delete buttons should be shown on rows */
  allowDelete?: boolean;
  /** Handler for delete button click on a row */
  onDeleteEntry?: (index: number) => void;
};

/**
 * Organization and role inputs component.
 * Allows users to add multiple organization/role affiliations.
 *
 * This is a pure UI component - works with or without react-hook-form.
 *
 * @example
 * <AffiliationList
 *   name="affiliations"
 *   value={affiliations}
 *   onChange={setAffiliations}
 *   organizationOptions={orgOptions}
 *   roleOptions={roleOptions}
 *   maxItems={3}
 * />
 */
export function AffiliationList({
  name,
  value = [],
  onChange,
  onBlur,
  error,
  "aria-describedby": ariaDescribedBy,
  "aria-required": ariaRequired,
  disabled,
  maxItems = 1,
  minItems = 1,
  className,
  organizationOptions = [],
  roleOptions = [],
  isLoading = false,
  onCreateOrganization,
  allowDelete = false,
  onDeleteEntry,
}: Props) {
  // Progressive rendering: start with minItems rows, add one more when the last visible row is complete
  const isComplete = (entry: AffiliationEntry) =>
    !!(entry.organization && entry.role);
  const completedCount = value.filter(isComplete).length;
  const effectiveMinItems = Math.min(minItems, maxItems);
  const visibleCount = Math.min(
    Math.max(effectiveMinItems, completedCount + 1),
    maxItems
  );
  const entries = Array.from(
    { length: visibleCount },
    (_, i) => value[i] || {}
  );

  const handleEntryChange = (index: number, entry: AffiliationEntry) => {
    const newValue = [...entries];
    newValue[index] = entry;
    onChange?.(newValue);
  };

  return (
    <div
      className={twMerge(
        "flex flex-col gap-6",
        error && "border-error-600",
        disabled && "opacity-50 pointer-events-none",
        className
      )}
      data-testid={`affiliation-list-${name}`}
      aria-disabled={disabled || undefined}
      aria-invalid={error || undefined}
    >
      {entries.map((entry, i) => {
        // Show delete button if allowDelete is enabled and row has content
        const hasContent = !!(entry.organization || entry.role);
        const canDelete = allowDelete && hasContent;

        return (
          <AffiliationRow
            key={i}
            namePrefix={`${name}.${i}`}
            value={entry}
            onChange={(newEntry) => handleEntryChange(i, newEntry)}
            onBlur={onBlur}
            error={error}
            aria-describedby={ariaDescribedBy}
            aria-required={ariaRequired}
            disabled={disabled}
            organizationOptions={organizationOptions}
            roleOptions={roleOptions}
            isLoading={isLoading}
            onCreateOrganization={onCreateOrganization}
            canDelete={canDelete}
            onDelete={() => onDeleteEntry?.(i)}
          />
        );
      })}
    </div>
  );
}

export default AffiliationList;
